import React from 'react';
import { useTranslation } from 'react-i18next';

const SlotCard = ({ slot, onVote, onUnvote, onConfirm, onDelete, onEdit, onNotAvailable, userIsAdmin }) => {
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = React.useState(!!slot.user_not_available);
    const isFull = slot.current_votes >= slot.max_players;
    const isReady = slot.reached_min_at !== null;

    if (slot.user_not_available && isCollapsed) {
        return (
            <div className="slot-card collapsed" onClick={() => setIsCollapsed(false)} style={{ cursor: 'pointer', padding: '10px 20px', borderLeft: '4px solid #ccc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888', fontSize: '0.9rem' }}>
                        🚫 <strong>{t('not_available')}:</strong> {new Date(slot.start_time).toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: '2-digit' })} {new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#1976d2' }}>{t('expand_for_details')}</span>
                </div>
            </div>
        );
    }

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return null;
        const R = 6371; // km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return (R * c).toFixed(1);
    };

    const distance = slot.user_coords && slot.user_coords.lat && slot.lat
        ? calculateDistance(slot.user_coords.lat, slot.user_coords.lng, slot.lat, slot.lng)
        : null;

    return (
        <div className={`slot-card ${isFull ? 'full' : ''} ${isReady ? 'ready' : ''} ${slot.user_not_available ? 'not-available' : ''}`}>
            {slot.user_not_available && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', margin: '-24px -24px 15px -24px', padding: '10px 24px', borderBottom: '1px solid #ffcdd2' }}>
                    <span style={{ color: '#c62828', fontWeight: 'bold', fontSize: '0.85rem' }}>{t('declared_not_available')}</span>
                    <button onClick={() => setIsCollapsed(true)} className="secondary-btn" style={{ padding: '4px 8px', fontSize: '0.7rem' }}>{t('compact')}</button>
                </div>
            )}
            {isReady && (
                <div style={{ marginBottom: '5px' }}>
                    <span className="ready-badge">{t('ready_for_confirmation')}</span>
                    {slot.priority_rank && (
                        <span className="ready-priority">{t('priority_quorum', { rank: slot.priority_rank })}</span>
                    )}
                </div>
            )}
            <div className="time-info">
                <div className="match-time-main">
                    <div className="match-date">
                        {new Date(slot.start_time).toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                    <div className="match-time">{new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                {slot.location && (
                    <div className="location-info">
                        <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(slot.address ? `${slot.location}, ${slot.address}` : slot.location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="location-link"
                        >
                            📍 {slot.location}
                            {slot.address && <span className="address-text"> - {slot.address}</span>}
                        </a>
                        {distance && (
                            <span style={{ marginLeft: '10px', color: '#1976d2', fontWeight: 'bold', fontSize: '0.8rem', minWidth: '75px', textAlign: 'right' }}>
                                🚗 {distance} km
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="vote-count" style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontWeight: '600' }}>{slot.current_votes} / {slot.max_players} {t('members')}</span>
                    {slot.price && (
                        <span style={{ fontSize: '0.85rem', color: '#666', background: '#f5f5f5', padding: '2px 8px', borderRadius: '4px' }}>
                            💰 {parseFloat(slot.price).toFixed(2)}€ (<strong>{(parseFloat(slot.price) / slot.max_players).toFixed(2)}€ {t('per_person')}</strong>)
                        </span>
                    )}
                </div>
                {slot.voters && slot.voters.length > 0 && (
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>
                        {slot.voters.map((v, i) => (
                            <span key={v.id || v.external_name} style={v.is_external ? { fontStyle: 'italic', opacity: 0.8 } : {}}>
                                {v.is_external ? v.external_name : `${v.first_name} ${v.last_name}`}
                                {v.is_guest && <span title={t('scouted_player')} style={{ marginLeft: '4px' }}>🌟</span>}
                                {v.roles && v.roles.length > 0 && <small style={{ color: '#666', fontSize: '0.75rem', opacity: 0.8 }}> ({v.roles.map(r => t(r)).join('/')})</small>}
                                {i < slot.voters.length - 1 ? ', ' : ''}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                <button
                    onClick={() => slot.user_has_voted ? onUnvote(slot.id) : onVote(slot.id)}
                    className={`vote-btn ${slot.user_has_voted ? 'voted' : 'primary'}`}
                    style={{ flex: 1 }}
                >
                    {slot.user_has_voted ? t('remove_vote') : (isFull ? t('waitlist_placeholder') : t('join_vote'))}
                </button>

                {!slot.user_has_voted && !slot.user_not_available && (
                    <button
                        onClick={() => onNotAvailable(slot.id)}
                        className="danger-btn outline"
                    >
                        {t('cannot_participate')}
                    </button>
                )}
            </div>

            {userIsAdmin && (
                <div className="admin-actions">
                    <button
                        onClick={() => onEdit(slot.id)}
                        className="secondary-btn"
                    >
                        📝 {t('edit')}
                    </button>
                    <button
                        onClick={() => onConfirm(slot.id)}
                        className="admin-confirm-btn"
                    >
                        {t('confirm_match')}
                    </button>
                    <button
                        onClick={() => onDelete(slot.proposal_id, slot.id)}
                        className="danger-btn"
                    >
                        🗑️ {t('delete_proposal')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SlotCard;
